import React, { useState, useEffect, useRef } from 'react';

// Free, fast suggestions using Photon API, filtering only place features
const fetchLocationSuggestions = async (query) => {
  try {
    const endpoint = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=10`;
    const res = await fetch(endpoint);
    const data = await res.json();
    return data.features
      .filter(f => f.properties.osm_key === 'place')
      .map(f => {
        const { name, country } = f.properties;
        return country ? `${name}, ${country}` : name;
      });
  } catch {
    return [];
  }
};

const StepDestination = ({ data, updateData, skip, goBack }) => {
  const [from, setFrom] = useState(data.from || '');
  const [to, setTo] = useState(data.destination || '');
  const [suggestionsFrom, setSuggestionsFrom] = useState([]);
  const [suggestionsTo, setSuggestionsTo] = useState([]);
  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);

  const fromRef = useRef(null);
  const toRef = useRef(null);

  // Fetch suggestions for 'from'
  useEffect(() => {
    let active = true;
    if (from.length >= 3 && showFromSuggestions) {
      fetchLocationSuggestions(from).then(list => {
        if (active) setSuggestionsFrom(list);
      });
    } else {
      setSuggestionsFrom([]);
    }
    return () => { active = false; };
  }, [from, showFromSuggestions]);

  // Fetch suggestions for 'to'
  useEffect(() => {
    let active = true;
    if (to.length >= 3 && showToSuggestions) {
      fetchLocationSuggestions(to).then(list => {
        if (active) setSuggestionsTo(list);
      });
    } else {
      setSuggestionsTo([]);
    }
    return () => { active = false; };
  }, [to, showToSuggestions]);

  const handleFromSelect = (loc) => {
    setFrom(loc);
    setShowFromSuggestions(false);
    setSuggestionsFrom([]);
    fromRef.current.blur();
  };

  const handleToSelect = (loc) => {
    setTo(loc);
    setShowToSuggestions(false);
    setSuggestionsTo([]);
    toRef.current.blur();
  };

  const handleNext = () => {
    if (from && to) {
      updateData('from', from);
      updateData('destination', to);
    }
  };

  const isNextDisabled = !(from.trim() && to.trim());

  return (
    <div className="bg-white/60 backdrop-blur p-6 rounded-xl shadow-lg relative">
      <h2 className="text-xl font-semibold mb-4">Where are you traveling from?</h2>
      <div className="relative mb-6">
        <input
          ref={fromRef}
          type="text"
          className="w-full p-3 border rounded-lg"
          placeholder="Start typing your departure city"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          onFocus={() => setShowFromSuggestions(true)}
        />
        {suggestionsFrom.length > 0 && (
          <ul className="absolute bg-white w-full border rounded-lg mt-1 max-h-40 overflow-auto z-10">
            {suggestionsFrom.map((loc, idx) => (
              <li
                key={idx}
                onMouseDown={() => handleFromSelect(loc)}
                className="p-2 hover:bg-gray-100 cursor-pointer"
              >
                {loc}
              </li>
            ))}
          </ul>
        )}
      </div>

      <h2 className="text-xl font-semibold mb-4">Where are you traveling to?</h2>
      <div className="relative mb-6">
        <input
          ref={toRef}
          type="text"
          className="w-full p-3 border rounded-lg"
          placeholder="Start typing your destination city"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          onFocus={() => setShowToSuggestions(true)}
        />
        {suggestionsTo.length > 0 && (
          <ul className="absolute bg-white w-full border rounded-lg mt-1 max-h-40 overflow-auto z-10">
            {suggestionsTo.map((loc, idx) => (
              <li
                key={idx}
                onMouseDown={() => handleToSelect(loc)}
                className="p-2 hover:bg-gray-100 cursor-pointer"
              >
                {loc}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex justify-end items-center space-x-4">
        {goBack && (
          <button
            onClick={goBack}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
          >
            Back
          </button>
        )}
        <button
          onClick={handleNext}
          disabled={isNextDisabled}
          className={`px-6 py-2 rounded-lg transition ${isNextDisabled ? 'bg-blue-300 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
        >
          Next
        </button>
        <button
          onClick={skip}
          className="px-6 py-2 bg-transparent border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
        >
          Skip
        </button>
      </div>
    </div>
  );
};

export default StepDestination;
